import { useSEO } from "@/hooks/use-seo";
import { ChevronLeft } from "lucide-react";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy — Ayrshire Hub",
    description: "How Ayrshire Hub collects, uses and protects your personal data. UK GDPR compliant.",
    noIndex: false,
  });

  const updated = "12 May 2026";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors mb-6 -ml-1">
        <ChevronLeft size={18} /> Back
      </button>
      <h1 className="text-3xl font-bold text-primary mb-2">Privacy Policy</h1>
      <p className="text-slate-500 text-sm mb-8">Last updated: {updated}</p>

      <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">1. Who we are</h2>
          <p>
            Ayrshire Hub is a trading name of <strong>Scotdev Ltd</strong> (Company No. SC887365)
            ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>"),
            a company registered in Scotland. We operate the website at{" "}
            <a href="https://ayrshirehub.co.uk" className="text-primary underline">ayrshirehub.co.uk</a> and the Ayrshire Hub mobile app.
          </p>
          <p className="mt-2">
            Scotdev Ltd (SC887365) is the data controller for personal data collected through this service.
          </p>
          <p className="mt-2">
            For data protection queries, contact us at: <a href="mailto:privacy@ayrshirehub.co.uk" className="text-primary underline">privacy@ayrshirehub.co.uk</a>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">2. What data we collect and why</h2>
          <p>We collect only what we need to provide and improve our services:</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-3 border border-slate-200 font-semibold">Data</th>
                  <th className="text-left p-3 border border-slate-200 font-semibold">Why we collect it</th>
                  <th className="text-left p-3 border border-slate-200 font-semibold">Legal basis</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Name & email address", "Create and manage your account, send notifications", "Contract (providing the service)"],
                  ["Password (hashed)", "Authenticate you securely. We never store your plain password.", "Contract"],
                  ["Town / location", "Show you relevant local content. Device location is processed on-device only and never sent to our servers.", "Legitimate interests"],
                  ["Phone number", "Optional contact detail for your profile", "Consent"],
                  ["Profile photo / bio", "Build your community profile", "Consent"],
                  ["Push notification tokens", "Deliver push notifications you have consented to", "Consent"],
                  ["Community posts & comments", "Operate the community board", "Contract"],
                  ["Job quotes & leads", "Connect residents with tradespeople", "Contract"],
                  ["Payment data", "Processed by Stripe on their servers. We store only payment status.", "Contract"],
                  ["Usage logs", "Security, debugging, and service improvement", "Legitimate interests"],
                ].map(([data, why, basis]) => (
                  <tr key={data} className="even:bg-slate-50">
                    <td className="p-3 border border-slate-200 font-medium">{data}</td>
                    <td className="p-3 border border-slate-200">{why}</td>
                    <td className="p-3 border border-slate-200">{basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">3. How we use your data</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Providing and personalising the Ayrshire Hub platform</li>
            <li>Sending service emails (account confirmation, password reset, lead alerts)</li>
            <li>Sending push notifications you have enabled on your device</li>
            <li>Moderating content and enforcing our Terms of Service</li>
            <li>Processing payments for paid features (via Stripe)</li>
            <li>Analysing aggregate, anonymous usage to improve the service</li>
            <li>Complying with legal obligations</li>
          </ul>
          <p className="mt-3">We do <strong>not</strong> sell your personal data to third parties.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">4. Who we share data with</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Stripe</strong> — payment processing. Stripe is PCI-DSS compliant and processes card data on their servers under their own privacy policy.</li>
            <li><strong>Expo</strong> — push notification delivery for the mobile app.</li>
            <li><strong>Apple Push Notification Service (APNs)</strong> — push notifications on iOS devices.</li>
            <li><strong>OpenStreetMap / Overpass API</strong> — anonymised bus stop lookups (no personal data sent).</li>
            <li><strong>Realtime Trains / Traveline</strong> — live transport data (no personal data sent).</li>
            <li><strong>Law enforcement / regulators</strong> — only where required by law or to protect the safety of users.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">5. How long we keep your data</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account data</strong>: retained while your account is active, and for 30 days after deletion to allow recovery.</li>
            <li><strong>Community posts</strong>: retained until you delete them, or up to 12 months after account deletion.</li>
            <li><strong>Transaction records</strong>: retained for 7 years to comply with UK financial record-keeping obligations.</li>
            <li><strong>Server logs</strong>: retained for 90 days for security and debugging purposes.</li>
            <li><strong>Password reset tokens</strong>: automatically expire after 1 hour and are removed within 30 days.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">6. Your rights</h2>
          <p>Under UK GDPR, you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong>Access</strong> the personal data we hold about you</li>
            <li><strong>Rectify</strong> inaccurate data</li>
            <li><strong>Erase</strong> your data (the "right to be forgotten"), subject to legal obligations</li>
            <li><strong>Restrict</strong> our processing in certain circumstances</li>
            <li><strong>Port</strong> your data to another service</li>
            <li><strong>Object</strong> to processing based on legitimate interests</li>
            <li><strong>Withdraw consent</strong> at any time where processing is consent-based</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, email us at{" "}
            <a href="mailto:privacy@ayrshirehub.co.uk" className="text-primary underline">privacy@ayrshirehub.co.uk</a>.
            We will respond within 30 days. You can also delete your account from your Account Settings page.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">7. Cookies</h2>
          <p>
            We use only essential cookies required for the service to function (authentication tokens stored in localStorage).
            We do not use tracking or advertising cookies. See our{" "}
            <a href="/cookie-policy" className="text-primary underline">Cookie Policy</a> for full details.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">8. Security</h2>
          <p>
            We use industry-standard security measures including encrypted HTTPS connections, bcrypt password hashing,
            signed JWT tokens, rate limiting on authentication endpoints, and HTTP security headers.
            No method of transmission over the internet is 100% secure; we cannot guarantee absolute security but we take
            reasonable precautions to protect your data.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">9. International transfers</h2>
          <p>
            Your data is processed and stored within the United Kingdom and European Economic Area.
            Where third-party processors (such as Stripe or Expo) operate outside the UK/EEA, they do so under
            appropriate safeguards including Standard Contractual Clauses or UK adequacy decisions.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">10. Children</h2>
          <p>
            Ayrshire Hub is intended for users aged 16 and over. We do not knowingly collect personal data from
            children under 16. If you believe a child has provided us with personal data, please contact us and
            we will delete it promptly.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">11. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. We will notify registered users of material changes
            by email. Continued use of the service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">12. Contact & complaints</h2>
          <p>
            For any data protection queries, contact us at{" "}
            <a href="mailto:privacy@ayrshirehub.co.uk" className="text-primary underline">privacy@ayrshirehub.co.uk</a>.
          </p>
          <p className="mt-2">
            You have the right to lodge a complaint with the UK's data protection regulator, the{" "}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Information Commissioner's Office (ICO)
            </a>{" "}
            at ico.org.uk or by calling 0303 123 1113.
          </p>
        </section>

      </div>
    </div>
  );
}
